import { Box, CardContent, Grid } from "@mui/material";
import { MprcCard, MprcContainer, MprcFormSubHeader, MprcLink, MprcRequestErrorReport, MprcRequestIndicator, MprcTypography } from "components";
import { MprcPinCodeField, MprcTwoFactorAppPinCodeHandle } from "components/MprcPinCodeField";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SessionService } from "services/sessionService";

interface IProps {
    onClose: (result: "cancel" | "skipped" | "validaded" | "emailAuthenticator") => void;
}

export const TwoFactorAppPinCode = (props: IProps) => {
    const { onClose } = props;

    const api = useCoreApi();
    const { t } = useTranslation();
    const [otp, setOtp] = useState('');
    const [checking, setChecking] = useState(false);
    const [viewError, setViewError] = useState<any>(null);
    const [validationFailed, setValidationFailed] = useState(false);
    const { config: { twoFactorOptions } } = useSession();
    const pincodeRef = useRef<MprcTwoFactorAppPinCodeHandle | null>(null);

    const handleChange = (newValue: string) => {
        setOtp(newValue);
        setValidationFailed(false);
    };

    const handleComplete = async (pinCode: string) => {
        setChecking(true);
        setValidationFailed(false);
        try {
            var response = await api.v1.account.appAuthenticatorValidateCode({ pinCode });
            if (response.data === true) {
                onClose("validaded");
            }
            else {
                setValidationFailed(true);
                setOtp("");
                pincodeRef.current?.focusFirstBox();
            }
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setChecking(false);
        }
    };

    return (
        <Grid container direction="column" justifyContent="center" alignItems="center" spacing={2} >
            <Grid size="grow">
                <img
                    src={SessionService.current.appImages.highlightImageUrl}
                    width="360"
                />
            </Grid>
            <Grid size="grow">

                <MprcCard sx={{ maxWidth: 450 }}>
                    <CardContent>
                        <MprcRequestIndicator type="request" visible={checking} />

                        {viewError
                            ? <MprcRequestErrorReport error={viewError} />
                            : <>
                                <MprcFormSubHeader
                                    title={
                                        <center>
                                            <Box sx={{ minHeight: 32 }}>
                                                {t("Auth.Login.2FA.APP.PinCode.EnterPinCodeTitle")}
                                            </Box>
                                        </center>}
                                    subtitle={
                                        <center>
                                            {t("Auth.Login.2FA.APP.PinCode.Body1")}
                                            <br />
                                            {t("Auth.Login.2FA.APP.PinCode.Body2")}
                                            <br />
                                            {t("Auth.Login.2FA.APP.PinCode.Body3")}
                                        </center>
                                    }
                                    margin="dense"
                                />

                                <center>
                                    <Box sx={{ mt: 2, mb: 2, width: "85%" }}>
                                        <MprcPinCodeField
                                            ref={pincodeRef}
                                            autoFocus
                                            isNumeric
                                            length={6}
                                            value={otp}
                                            onComplete={handleComplete}
                                            onChange={handleChange}
                                        />
                                    </Box>

                                    {validationFailed &&
                                        <MprcContainer>
                                            <MprcTypography variant="captionHeader" color="error">
                                                {t("Auth.Login.2FA.APP.PinCode.InvalidCodeText")}
                                            </MprcTypography>
                                        </MprcContainer>
                                    }
                                </center>

                                {
                                    twoFactorOptions?.emailAuthenticatorEnabled === true
                                        ? <Box sx={{ mt: 4 }}>
                                            <MprcContainer margin="dense">
                                                <MprcTypography variant="subtitle1" color="muted">
                                                    {t("Auth.Login.2FA.APP.PinCode.AltMethodSubtitle")}
                                                </MprcTypography>
                                            </MprcContainer>

                                            <MprcLink onClick={() => onClose("emailAuthenticator")}>
                                                <span>- </span>
                                                <span>{t("Auth.Login.2FA.APP.PinCode.SendCodeByEmailText")}</span>
                                            </MprcLink>
                                        </Box>
                                        : <MprcLink onClick={() => onClose("cancel")}>
                                            {t("Auth.Login.2FA.APP.PinCode.CancelText")}
                                        </MprcLink>
                                }
                            </>
                        }
                    </CardContent>
                </MprcCard>
            </Grid>
        </Grid >
    );
};